import { useEffect, useState } from "react";
import { Handshake, Loader2, Mail, MapPin, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const MODEL_LABELS = {
  franchise: "Franchise Partner", associate: "Business Associate",
  agent: "Agent / Booking Partner", local: "Local / Small-Town Partner",
};
const STATUS_STYLES = {
  new: "bg-saffron/15 text-saffron", contacted: "bg-blue-500/15 text-blue-400", closed: "bg-emerald-500/15 text-emerald-400",
};

export default function AdminPartnerEnquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/admin/partner-enquiries${filter !== "all" ? `?status=${filter}` : ""}`)
      .then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const setStatus = async (e, status) => {
    try { await api.put(`/admin/partner-enquiries/${e.id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const del = async (e) => {
    if (!window.confirm(`Delete enquiry from "${e.name}"?`)) return;
    try { await api.delete(`/admin/partner-enquiries/${e.id}`); toast.success("Enquiry deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="admin-partner-enquiries">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Partner Enquiries</h1>
          <p className="mt-1 text-sm text-slate-500">Applications submitted on the Partner With Us page.</p>
        </div>
        <div className="flex gap-1.5">
          {["all", "new", "contacted", "closed"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`enquiry-filter-${s}`}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold capitalize transition-colors ${filter === s ? "bg-saffron text-white" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
      ) : !items.length ? (
        <div className="mt-6 rounded-xl border border-adminborder bg-admincard p-14 text-center" data-testid="admin-enquiries-empty">
          <Handshake className="mx-auto h-10 w-10 text-slate-600" />
          <p className="mt-3 text-sm font-semibold text-slate-300">No partner enquiries {filter !== "all" ? `with status "${filter}"` : "yet"}</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 lg:grid-cols-2" data-testid="admin-enquiry-list">
          {items.map((e) => (
            <div key={e.id} className="rounded-xl border border-adminborder bg-admincard p-5" data-testid={`enquiry-card-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-white">{e.name}</p>
                  <p className="mt-0.5 text-[11px] text-slate-500">{(e.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
                <span className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[e.status] || STATUS_STYLES.new}`}>{e.status}</span>
              </div>
              <div className="mt-3 space-y-1.5 text-xs text-slate-400">
                <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-saffron" /><a className="hover:text-saffron" href={`tel:+91${e.phone}`}>{e.phone}</a>{e.email && <span className="flex items-center gap-1.5 truncate"><Mail className="h-3.5 w-3.5 text-saffron" />{e.email}</span>}</p>
                <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-saffron" />{e.city}, {e.state}{e.preferred_location ? ` · Prefers ${e.preferred_location}` : ""}</p>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="rounded-full bg-saffron/10 px-2.5 py-1 text-[10px] font-bold text-saffron">{MODEL_LABELS[e.preferred_model] || e.preferred_model}</span>
                {e.investment_range && <span className="rounded-full bg-white/5 px-2.5 py-1 text-[10px] font-semibold text-slate-300">{e.investment_range}</span>}
                {e.experience && <span className="rounded-full bg-white/5 px-2.5 py-1 text-[10px] font-semibold text-slate-300">{e.experience}</span>}
                {e.current_business && <span className="rounded-full bg-white/5 px-2.5 py-1 text-[10px] font-semibold text-slate-300">{e.current_business}</span>}
              </div>
              {e.message && <p className="mt-3 rounded-lg bg-adminbg p-3 text-xs leading-relaxed text-slate-400">{e.message}</p>}
              <div className="mt-4 flex items-center gap-2">
                <select value={e.status} onChange={(ev) => setStatus(e, ev.target.value)} data-testid={`enquiry-status-${e.id}`}
                        className="rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-xs font-semibold text-slate-200 outline-none focus:border-saffron">
                  <option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option>
                </select>
                <button onClick={() => del(e)} data-testid={`enquiry-delete-${e.id}`}
                        className="ml-auto rounded-lg bg-red-500/10 p-2 text-red-400 hover:bg-red-500/20"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
